import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { unitTopicBadgeClass } from '../unitCardTheme';
import type { InstructionStepDto } from './types';
import { getEffectiveInstructionSteps, stripLeadingStepNumberFromTitle } from './instructionStepUtils';
import { InstructionStepKindKey } from './lessonStepState';

function stepKindLabel(kind?: string): string | null {
    switch (kind) {
        case InstructionStepKindKey.WrapUp:
            return 'Wrap up';
        case InstructionStepKindKey.Review:
            return 'Review';
        default:
            return null;
    }
}

function stepLabel(step: InstructionStepDto, index: number): string {
    const number = step.e251_StepNumber ?? index + 1;
    const summary =
        stripLeadingStepNumberFromTitle(step.e251_Summary) || `Step ${number}`;
    return `${number}. ${summary}`;
}

interface ExpandableInstructionStepListProps {
    steps?: InstructionStepDto[];
    className?: string;
    defaultExpanded?: boolean;
}

/** Instruction-step chips that expand detailed tutor guidance under each chip. */
export function ExpandableInstructionStepList({
    steps,
    className,
    defaultExpanded = false,
}: ExpandableInstructionStepListProps) {
    const ordered = React.useMemo(() => getEffectiveInstructionSteps(steps), [steps]);
    const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(() => {
        if (!defaultExpanded) return new Set();
        const effective = getEffectiveInstructionSteps(steps);
        return new Set(
            effective
                .filter(s => (s.e251_DetailedInstructions?.trim() ?? '').length > 0)
                .map((s, i) => s.e251_StepId ?? `step-${i}`)
        );
    });

    if (ordered.length === 0) return null;

    const toggleStep = (rowKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(rowKey)) {
                next.delete(rowKey);
            } else {
                next.add(rowKey);
            }
            return next;
        });
    };

    return (
        <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
            {ordered.map((step, index) => {
                const rowKey = step.e251_StepId ?? `step-${index}`;
                const detail = step.e251_DetailedInstructions?.trim() ?? '';
                const canExpand = detail.length > 0;
                const isExpanded = canExpand && expandedKeys.has(rowKey);
                const kind = stepKindLabel(step.e251_StepKind);
                const duration =
                    typeof step.e251_DurationMinutes === 'number' && step.e251_DurationMinutes > 0
                        ? `${step.e251_DurationMinutes} min`
                        : null;
                const label = stepLabel(step, index);

                return (
                    <div key={rowKey} className="flex min-w-0 flex-col items-start gap-1">
                        {canExpand ? (
                            <button
                                type="button"
                                aria-expanded={isExpanded}
                                onClick={e => toggleStep(rowKey, e)}
                                onMouseDown={e => e.stopPropagation()}
                                className={cn(
                                    unitTopicBadgeClass,
                                    'inline-flex max-w-full cursor-pointer items-center gap-1 text-left hover:bg-silver-tree-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blumine focus-visible:ring-offset-1'
                                )}
                            >
                                {isExpanded ? (
                                    <ChevronDown size={12} className="shrink-0 opacity-90" aria-hidden />
                                ) : (
                                    <ChevronRight size={12} className="shrink-0 opacity-90" aria-hidden />
                                )}
                                <span>{label}</span>
                                {kind ? (
                                    <span className="shrink-0 opacity-80">({kind})</span>
                                ) : null}
                                {duration ? (
                                    <span className="shrink-0 opacity-80">· {duration}</span>
                                ) : null}
                            </button>
                        ) : (
                            <span className={cn(unitTopicBadgeClass, 'inline-flex max-w-full items-center gap-1')}>
                                <span>{label}</span>
                                {kind ? (
                                    <span className="shrink-0 opacity-80">({kind})</span>
                                ) : null}
                                {duration ? (
                                    <span className="shrink-0 opacity-80">· {duration}</span>
                                ) : null}
                            </span>
                        )}

                        {isExpanded && (
                            <div
                                className="w-full rounded-md border border-gray-200/80 bg-white/80 px-2.5 py-2"
                                onClick={e => e.stopPropagation()}
                                onMouseDown={e => e.stopPropagation()}
                            >
                                <p className="whitespace-pre-wrap text-xs leading-relaxed text-gray-700">
                                    {detail}
                                </p>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
